// The web / PWA implementation of the Platform seam (D-133; plan:
// 02_ENGINEERING/Platform_Abstraction_Plan.md). NOT yet wired into any build —
// index.ts still binds the extension impl. It exists to PROVE the interface is
// genuinely dual-implementable and to seed the web shell: when the web build
// exists, index.ts selects this at build time and the whole shared core runs
// unchanged on app.tickahead.com. No chrome.* here — only web platform APIs.
import type { Platform } from "./types.js";

// The control-plane data proxy (D-133). A web origin has no host_permissions, so
// public market-data fetches are CORS-blocked; the proxy forwards them with CORS
// headers. Same backend the license service runs on (its custom domain).
const DATA_PROXY = "https://api.tickahead.com/v1/data/proxy";

// --- IndexedDB key/value store (the web home for what chrome.storage.local
// held in the extension; big enough for the journal, unlike localStorage). ---
const DB_NAME = "tickahead";
const STORE = "kv";
let dbPromise: Promise<IDBDatabase> | null = null;

function db(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  return db().then(
    (d) =>
      new Promise<T>((resolve, reject) => {
        const req = run(d.transaction(STORE, mode).objectStore(STORE));
        req.onsuccess = () => resolve(req.result as T);
        req.onerror = () => reject(req.error);
      }),
  );
}

// Change notification — the web analog of chrome.storage.onChanged, which fires
// in EVERY context including the writer. BroadcastChannel covers OTHER tabs but
// does NOT deliver to the sender, so we also fan out on a same-tab local bus.
// (chrome.storage.onChanged fires in the writing context too, and the app relies
// on that — e.g. the builder overlay writing an action a same-tab listener applies.)
const changeBus = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("tickahead-storage") : null;
const localBus = new EventTarget();
function announce(key: string): void {
  changeBus?.postMessage(key); // other tabs
  localBus.dispatchEvent(new CustomEvent("change", { detail: key })); // this tab
}

export const webPlatform: Platform = {
  kind: "web",
  storage: {
    async get<T>(key: string, fallback: T): Promise<T> {
      const v = await tx<unknown>("readonly", (s) => s.get(key));
      return (v as T | undefined) ?? fallback;
    },
    async set<T>(key: string, value: T): Promise<void> {
      await tx("readwrite", (s) => s.put(value, key));
      announce(key);
    },
    onChange<T>(key: string, cb: (value: T) => void): () => void {
      const reread = (): void => void tx<unknown>("readonly", (s) => s.get(key)).then((v) => cb(v as T));
      const busHandler = (e: Event): void => { if ((e as MessageEvent).data === key) reread(); };
      const localHandler = (e: Event): void => { if ((e as CustomEvent).detail === key) reread(); };
      changeBus?.addEventListener("message", busHandler); // other tabs
      localBus.addEventListener("change", localHandler); // this tab
      return () => {
        changeBus?.removeEventListener("message", busHandler);
        localBus.removeEventListener("change", localHandler);
      };
    },
    async remove(keys: string | string[]): Promise<void> {
      const list = Array.isArray(keys) ? keys : [keys];
      for (const k of list) {
        await tx("readwrite", (s) => s.delete(k));
        announce(k);
      }
    },
    async getAll(): Promise<Record<string, unknown>> {
      const keys = await tx<IDBValidKey[]>("readonly", (s) => s.getAllKeys());
      const values = await tx<unknown[]>("readonly", (s) => s.getAll());
      const out: Record<string, unknown> = {};
      keys.forEach((k, i) => (out[String(k)] = values[i]));
      return out;
    },
  },
  // In the web shell the builder is a route, not a browser tab.
  openBuilder: (symbol: string): void => {
    location.hash = `#/build/${encodeURIComponent(symbol)}`;
  },
  // Bundler-relative asset path (the web build serves assets from the app root).
  assetUrl: (path: string): string => path,
  // Build-injected at bundle time for the web shell; placeholder until then.
  appVersion: (): string => "web",
  notify: async (title: string, body: string): Promise<void> => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      try {
        await Notification.requestPermission();
      } catch {
        /* denied / unavailable — silently skip */
      }
    }
    if (Notification.permission === "granted") new Notification(title, { body });
  },
  // Route public market-data fetches through the control-plane proxy (CORS).
  dataFetch: (url: string, init?: RequestInit): Promise<Response> =>
    fetch(`${DATA_PROXY}?url=${encodeURIComponent(url)}`, init),
  // No cross-tab integration in the web shell — that's the extension's exclusive
  // capability. UI checks `platform.crossTab` and hides those features here.
  crossTab: null,
};
